import { useEffect, useState } from "react";
import { Download, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { apiService } from "../services/api.service";
import { MAX_SNAPSHOT_DAYS, type SnapshotJob } from "../lib/data-fetch";
import { Button } from "./ui/button";
import { Card } from "./ui/card";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "./ui/dialog";
import { AdvancedOptions, type FilterValues } from "./search/AdvancedOptions";

async function readResponse(response: Response): Promise<SnapshotJob> {
  const body = await response.json();
  if (!response.ok) throw new Error(body.error || "Export request failed");
  return body as SnapshotJob;
}

export function DataFetch({ ownerId }: { ownerId: string }) {
  const historyKey = `student-snapshot-requests:${ownerId}`;
  const [open, setOpen] = useState(false);
  const [orgs, setOrgs] = useState<{ id: string; name: string }[]>([]);
  const [filters, setFilters] = useState<FilterValues>({});
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [jobs, setJobs] = useState<SnapshotJob[]>([]);
  useEffect(() => {
    const controller = new AbortController();
    let ids: string[] = [];
    try {
      const saved: unknown = JSON.parse(localStorage.getItem(historyKey) || "[]");
      if (Array.isArray(saved)) ids = saved.filter((value): value is string => typeof value === "string" && /^[\da-f-]{36}$/.test(value)).slice(0, 20);
    } catch { /* Requests still work if browser storage is unavailable. */ }
    void Promise.all(ids.map(async (id) => {
      const response = await fetch(`/api/internal/admin/data-fetch/snapshots/${id}`, { credentials: "include", signal: controller.signal });
      if (response.status === 404) return null;
      return readResponse(response);
    })).then((restored) => {
      if (!controller.signal.aborted) setJobs((current) => [...current, ...restored.filter((job): job is SnapshotJob => job !== null && !current.some((value) => value.id === job.id))]);
    }).catch(() => {
      if (!controller.signal.aborted) toast.error("Could not restore export requests. Reload to retry.");
    });
    return () => controller.abort();
  }, [historyKey]);
  useEffect(() => {
    let current = true;
    apiService.getOrganizations().then((values) => { if (current) setOrgs(values); }).catch(() => toast.error("Could not load organizations"));
    return () => { current = false; };
  }, []);
  const runningIds = jobs.filter((job) => job.status === "running").map((job) => job.id).join(",");
  useEffect(() => {
    if (!runningIds) return;
    const controller = new AbortController();
    let busy = false;
    const poll = async () => {
      if (busy) return;
      busy = true;
      try {
        for (const id of runningIds.split(",")) {
          const job = await readResponse(await fetch(`/api/internal/admin/data-fetch/snapshots/${id}`, { credentials: "include", signal: controller.signal }));
          setJobs((values) => values.map((value) => value.id === id ? job : value));
        }
      } catch (error) {
        if (!controller.signal.aborted) toast.error(error instanceof Error ? error.message : "Could not read export status");
      } finally { busy = false; }
    };
    const timer = setInterval(() => void poll(), 5000);
    return () => { clearInterval(timer); controller.abort(); };
  }, [runningIds]);
  const submit = async () => {
    setSubmitting(true);
    try {
      const job = await readResponse(await fetch("/api/internal/admin/data-fetch/snapshots", {
        method: "POST", credentials: "include", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ organizationId: filters.organizationId, start, end }),
      }));
      setJobs((values) => [job, ...values]);
      try {
        const saved: unknown = JSON.parse(localStorage.getItem(historyKey) || "[]");
        const previous = Array.isArray(saved) ? saved.filter((id) => typeof id === "string") : [];
        localStorage.setItem(historyKey, JSON.stringify([job.id, ...previous.filter((id) => id !== job.id)].slice(0, 20)));
      } catch { toast.warning("This browser cannot save request history. Keep this tab open until your export finishes."); }
      setOpen(false);
      toast.success("Export started");
    } catch (error) { toast.error(error instanceof Error ? error.message : "Could not start export"); }
    finally { setSubmitting(false); }
  };
  return <div className="space-y-6 p-6">
    <Card className="p-5">
      <div className="flex items-center justify-between gap-4">
        <div><h2 className="font-semibold">Student data by organization and date range</h2><p className="text-sm text-muted-foreground">Export bite attempts, attempts with conversations, and exit tickets as three CSV files for students in classrooms taught by the organization's admin and its teachers.</p></div>
        <Button onClick={() => setOpen(true)}>Fetch</Button>
      </div>
    </Card>
    {jobs.map((job) => <Card key={job.id} className="space-y-3 p-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div><h3 className="font-medium">{orgs.find((org) => org.id === job.organizationId)?.name || job.organizationId}</h3><p className="text-sm text-muted-foreground">{job.start} through {job.end} · {job.status}</p><p className="text-xs text-muted-foreground">Request {job.id}</p></div>
        {job.status === "running" && <Loader2 className="size-5 animate-spin" aria-label="Export running" />}
        {job.status === "complete" && <Button variant="outline" asChild><a href={`/api/internal/admin/data-fetch/snapshots/${job.id}/files/all`}><Download className="mr-2 size-4" />Download all</a></Button>}
      </div>
      {job.error && <p role="alert" className="text-sm text-destructive">{job.error}</p>}
      {job.files.map((file) => <a key={file.index} className="flex items-center gap-2 break-all text-sm text-primary underline" href={`/api/internal/admin/data-fetch/snapshots/${job.id}/files/${file.index}`}><Download className="size-4 shrink-0" />{file.name} ({Math.ceil(file.bytes / 1024)} KB)</a>)}
    </Card>)}
    <Dialog open={open} onOpenChange={(next) => { if (!submitting) setOpen(next); }}>
      <DialogContent><DialogHeader><DialogTitle>Fetch student data</DialogTitle><DialogDescription>Start and end dates are inclusive, in UTC, and cover at most {MAX_SNAPSHOT_DAYS} days. Files expire after 24 hours.</DialogDescription></DialogHeader>
        <AdvancedOptions inline fields={[{ key: "organizationId", label: "Organization", emptyLabel: "Select an organization", options: orgs.map((org) => ({ value: org.id, label: org.name })) }]} values={filters} onChange={setFilters} />
        <div className="grid grid-cols-2 gap-3"><div className="space-y-2"><Label htmlFor="snapshot-start">Start date</Label><Input id="snapshot-start" type="date" value={start} onChange={(event) => setStart(event.target.value)} /></div><div className="space-y-2"><Label htmlFor="snapshot-end">End date</Label><Input id="snapshot-end" type="date" min={start} value={end} onChange={(event) => setEnd(event.target.value)} /></div></div>
        <DialogFooter><Button variant="outline" disabled={submitting} onClick={() => setOpen(false)}>Cancel</Button><Button disabled={submitting || !filters.organizationId || !start || !end || end < start} onClick={() => void submit()}>{submitting ? "Starting…" : "Fetch"}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  </div>;
}
